import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';

// Registrar componentes do ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Endereço do serviço Python de monitoramento
const CACHE_SERVICE_URL = 'http://localhost:5500';

export const DetailedCacheReport = ({ salonId }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interval, setInterval] = useState('day'); // day, week, month

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${CACHE_SERVICE_URL}/report/${salonId}`);
        if (response.data && response.data.status === 'success') {
          setReportData(response.data);
          setError(null);
        } else {
          throw new Error('Dados inválidos recebidos do serviço');
        }
      } catch (err) {
        console.error('Erro ao carregar relatório detalhado:', err);
        setError(err.message || 'Erro ao carregar relatório detalhado');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [salonId, interval]);

  // Função para formatar data/hora
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Data inválida';
    }
  };

  // Função para renderizar gráfico de linha da taxa de acerto ao longo do tempo
  const renderHitRateChart = () => {
    if (!reportData || !reportData.reports || reportData.reports.length === 0) {
      return <div className="p-4 text-center text-gray-500">Dados insuficientes para gerar gráfico</div>;
    }

    // Ordenar por timestamp (mais antigo primeiro)
    const sortedReports = [...reportData.reports].sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    const labels = sortedReports.map(report => {
      const date = new Date(report.date);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    });

    const hitRateData = sortedReports.map(report => report.hitRate * 100);
    const effectiveHitRateData = sortedReports.map(report => report.effectiveHitRate * 100);

    const data = {
      labels,
      datasets: [
        {
          label: 'Taxa de Acerto (%)',
          data: hitRateData,
          borderColor: 'rgb(53, 162, 235)',
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
          tension: 0.3,
        },
        {
          label: 'Taxa de Acerto Efetiva (%)',
          data: effectiveHitRateData,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.3,
        }
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Evolução da Taxa de Acerto',
        },
      },
      scales: {
        y: {
          min: 0,
          max: 100,
          title: {
            display: true,
            text: 'Taxa de Acerto (%)'
          }
        },
      }
    };

    return <Line data={data} options={options} />;
  };

  // Função para renderizar gráfico de pizza de tipos de acerto
  const renderHitTypesChart = () => {
    if (!reportData || !reportData.reports || reportData.reports.length === 0) {
      return <div className="p-4 text-center text-gray-500">Dados insuficientes para gerar gráfico</div>;
    }

    // Usar o relatório mais recente
    const latestReport = reportData.reports[0];
    
    // Calcular valores
    const memoryHits = latestReport.hits - (latestReport.staleHits || 0) - (latestReport.storageHits || 0);
    const staleHits = latestReport.staleHits || 0;
    const storageHits = latestReport.storageHits || 0;
    const misses = latestReport.misses || 0;

    const data = {
      labels: ['Acertos da Memória', 'Acertos Obsoletos', 'Acertos do Storage', 'Falhas'],
      datasets: [
        {
          data: [memoryHits, staleHits, storageHits, misses],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(255, 99, 132, 0.7)',
          ],
          borderColor: [
            'rgb(54, 162, 235)',
            'rgb(255, 159, 64)',
            'rgb(75, 192, 192)',
            'rgb(255, 99, 132)',
          ],
          borderWidth: 1,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Distribuição de Requisições por Tipo',
        },
      },
    };

    return <Pie data={data} options={options} />;
  };

  // Função para renderizar gráfico de barra do tamanho do cache ao longo do tempo
  const renderCacheSizeChart = () => {
    if (!reportData || !reportData.reports || reportData.reports.length === 0) {
      return <div className="p-4 text-center text-gray-500">Dados insuficientes para gerar gráfico</div>;
    }

    // Ordenar por timestamp (mais antigo primeiro)
    const sortedReports = [...reportData.reports].sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    const labels = sortedReports.map(report => {
      const date = new Date(report.date);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    });

    const cacheSizeData = sortedReports.map(report => report.size);

    const data = {
      labels,
      datasets: [
        {
          label: 'Tamanho do Cache (KB)',
          data: cacheSizeData,
          backgroundColor: 'rgba(153, 102, 255, 0.5)',
        }
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Evolução do Tamanho do Cache',
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Tamanho (KB)'
          }
        },
      }
    };

    return <Bar data={data} options={options} />;
  };

  // Exibir estado de carregamento
  if (loading) {
    return (
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Relatório Detalhado</h3>
        <div className="flex items-center justify-center py-4">
          <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
          <span className="ml-2">Carregando relatório...</span>
        </div>
      </div>
    );
  }

  // Exibir erro
  if (error) {
    return (
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Relatório Detalhado</h3>
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          <p className="font-bold">Erro ao carregar relatório</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Se não há dados
  if (!reportData || !reportData.reports || reportData.reports.length === 0) {
    return (
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Relatório Detalhado</h3>
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4">
          <p className="font-bold">Sem dados disponíveis</p>
          <p>Não há dados de cache suficientes para gerar um relatório detalhado.</p>
        </div>
      </div>
    );
  }

  // Calcular métricas gerais usando o relatório mais recente
  const latestReport = reportData.reports[0];
  const totalRequests = latestReport.totalRequests || (latestReport.hits + latestReport.misses);
  const savedRequests = latestReport.hits || 0;
  
  // Calcular economia estimada
  // Preço médio de Firestore: $0.036 por 100.000 leituras
  const firestoreCostPer100K = 0.036;
  const estimatedSavings = (savedRequests * firestoreCostPer100K) / 100000;

  return (
    <div className="bg-white rounded-lg">
      <h3 className="text-lg font-semibold mb-4">Relatório Detalhado para Salão #{salonId}</h3>
      
      {/* Seleção de intervalo */}
      <div className="flex mb-6">
        <button 
          onClick={() => setInterval('day')}
          className={`px-4 py-2 ${interval === 'day' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded-l`}>
          Dia
        </button>
        <button 
          onClick={() => setInterval('week')}
          className={`px-4 py-2 ${interval === 'week' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          Semana
        </button>
        <button 
          onClick={() => setInterval('month')}
          className={`px-4 py-2 ${interval === 'month' ? 'bg-blue-500 text-white' : 'bg-gray-200'} rounded-r`}>
          Mês
        </button>
      </div>
      
      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-green-600">Requisições Economizadas</p>
          <p className="text-3xl font-bold">{savedRequests.toLocaleString('pt-BR')}</p>
          <p className="text-xs text-green-500">{((savedRequests / totalRequests) * 100).toFixed(1)}% do total</p>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-600">Economia Estimada</p>
          <p className="text-3xl font-bold">R$ {estimatedSavings.toFixed(2)}</p>
          <p className="text-xs text-blue-500">Baseado no custo do Firestore</p>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-purple-600">Eficiência do Cache</p>
          <p className="text-3xl font-bold">{(latestReport.effectiveHitRate * 100).toFixed(1)}%</p>
          <p className="text-xs text-purple-500">Taxa de acerto efetiva</p>
        </div>
      </div>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <h4 className="text-md font-medium mb-2">Evolução da Taxa de Acerto</h4>
          {renderHitRateChart()}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h4 className="text-md font-medium mb-2">Distribuição de Requisições</h4>
          {renderHitTypesChart()}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h4 className="text-md font-medium mb-2">Evolução do Tamanho do Cache</h4>
          {renderCacheSizeChart()}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <h4 className="text-md font-medium mb-2">Histórico Recente</h4>
          <div className="max-h-80 overflow-y-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data/Hora</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taxa de Acerto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requisições</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamanho</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.reports.map((report, index) => (
                  <tr key={index}>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{formatDateTime(report.date)}</td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm">
                      <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        {(report.hitRate * 100).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      {report.totalRequests}
                      <span className="text-xs ml-1">
                        ({report.hits} / {report.misses})
                      </span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">{report.size} KB</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Última atualização */}
      <div className="text-right text-xs text-gray-500">
        Última atualização: {formatDateTime(reportData.reports[0]?.date)}
      </div>
    </div>
  );
};

export default DetailedCacheReport; 